/**
 * Turns a logged action ("products.update", "orders.fulfillments.create",
 * "terify.settings.general") into words for the activity log:
 * { verb: "updated", noun: "product" }.
 */
const VERBS: Record<string, string> = {
  create: "created",
  update: "updated",
  delete: "deleted",
  cancel: "cancelled",
  complete: "completed",
  capture: "captured",
  archive: "archived",
  unarchive: "unarchived",
  duplicate: "duplicated",
  publish: "published",
  unpublish: "unpublished",
  approve: "approved",
  reject: "rejected",
  hide: "hid",
  restore: "restored",
  revoke: "revoked",
  resend: "resent",
  send: "sent",
  test: "sent a test of",
  import: "imported",
  export: "exported",
  adjust: "adjusted",
  batch: "bulk-edited",
  bulk: "bulk-edited",
  accept: "accepted",
  transfer: "transferred",
  deactivate: "deactivated",
  reactivate: "reactivated",
  recover: "sent a recovery email for",
  refund: "refunded",
  refunds: "refunded",
  mark_as_paid: "marked as paid",
  skip: "skipped",
  pause: "paused",
  resume: "resumed",
  charge: "charged",
  run: "ran",
  reorder: "reordered",
  upload: "uploaded",
  uploads: "uploaded",
  revert: "reverted",
  signed_in: "signed in",
  signed_out: "signed out",
  password_changed: "changed their password",
  role_changed: "changed the role of",
  invited: "invited",
}

/** Plural/compound resource names that read badly when naively singularised. */
const NOUNS: Record<string, string> = {
  terify: "",
  admin: "",
  inventory_items: "inventory item",
  location_levels: "stock level",
  "product-options": "product option",
  product_options: "product option",
  shipping_options: "shipping rate",
  shipping_profiles: "shipping profile",
  stock_locations: "location",
  sales_channels: "sales channel",
  price_lists: "price list",
  api_keys: "API key",
  draft_orders: "draft order",
  gift_cards: "gift card",
  content: "",
  posts: "blog post",
  files: "file",
  regions: "market",
  promotions: "discount",
  campaigns: "discount campaign",
  customer_groups: "customer segment",
  users: "staff member",
  staff: "staff member",
  invites: "staff invite",
  apps: "app",
  settings: "settings",
  general: "general",
  checkout: "checkout",
  payments: "payment",
  brand: "brand",
  notifications: "notification template",
  homepage: "homepage",
  abandoned: "abandoned checkout",
  reviews: "review",
  upsells: "upsell",
  offers: "upsell offer",
  subscriptions: "subscription",
  plans: "subscription plan",
  taxes: "tax",
  countries: "country tax",
  provinces: "regional tax",
  zones: "shipping zone",
  markets: "market",
  online: "online store",
  password: "password",
  policies: "policy",
  pages: "page",
  menus: "menu",
  redirects: "redirect",
  custom_data: "custom data",
  annotations: "annotation",
  targets: "target",
  reports: "report",
  comments: "comment",
  fulfillments: "fulfilment",
  shipments: "shipment",
  returns: "return",
  claims: "claim",
  exchanges: "exchange",
  variants: "variant",
  options: "option",
  images: "image",
  addresses: "address",
}

const singular = (w: string) => {
  if (w in NOUNS) return NOUNS[w]
  const s = w.replace(/_/g, " ")
  if (s.endsWith("ies")) return s.slice(0, -3) + "y"
  if (s.endsWith("ses") || s.endsWith("xes")) return s.slice(0, -2)
  if (s.endsWith("s") && !s.endsWith("ss")) return s.slice(0, -1)
  return s
}

export function describeAction(action: string, method: string | null): { verb: string; noun: string } {
  const parts = action.split(".").filter(Boolean)
  const last = parts[parts.length - 1] ?? ""
  let verb: string
  let nouns: string[]
  if (last in VERBS) {
    verb = VERBS[last]
    nouns = parts.slice(0, -1)
  } else {
    // "terify.settings.general" (a settings page save) and other named endpoints
    verb = method === "DELETE" ? "deleted" : method === "POST" && /create|new/.test(last) ? "created" : "updated"
    nouns = parts
  }
  const words = nouns.map(singular).filter(Boolean)
  // "settings general" → "general settings"; otherwise the most specific noun.
  let noun: string
  if (words[0] === "settings" && words.length > 1) noun = `${words.slice(1).join(" ")} settings`
  else noun = words[words.length - 1] ?? ""
  return { verb, noun }
}

export function initials(name: string | null | undefined, email: string | null | undefined) {
  const src = (name || email || "?").trim()
  const parts = src.split(/[\s@._-]+/).filter(Boolean)
  return ((parts[0]?.[0] ?? "?") + (name && parts[1] ? parts[1][0] : "")).toUpperCase()
}
